'use client'
import { useEffect, useRef, useState } from 'react'

/**
 * Custom hook to detect scroll direction on the window object.
 * Returns two booleans: isScrollingDown & isScrollingUp.
 * When the user scrolls down, isScrollingDown will be true for one animation frame while isScrollingUp will be false and vice-versa.
 *
 * NOTE: In the first render both values are false.
 */
export default function useScrollDirection() {
  const [isScrollingDown, setIsScrollingDown] = useState(false)
  const [isScrollingUp, setIsScrollingUp] = useState(false)

  // Keep track of last scroll position without re-rendering on every scroll
  const lastScrollY = useRef(0)
  const ticking = useRef(false)

  useEffect(() => {
    const updateScrollDir = () => {
      const scrollY = window.scrollY || window.pageYOffset

      if (scrollY > lastScrollY.current) {
        setIsScrollingDown(true)
        setIsScrollingUp(false)
      } else if (scrollY < lastScrollY.current) {
        setIsScrollingDown(false)
        setIsScrollingUp(true)
      }
      lastScrollY.current = scrollY > 0 ? scrollY : 0
      ticking.current = false
    }

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDir)
        ticking.current = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return { isScrollingDown, isScrollingUp }
}
